import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { RelayState } from '../api/types';
import { vt } from '../theme';

export type PillState = RelayState | 'failed' | 'allow' | 'deny' | 'attention' | 'live';

const LABEL: Record<PillState, string> = {
  stopped: 'Stopped',
  starting: 'Starting',
  running: 'Running',
  stopping: 'Stopping',
  failed: 'Failed',
  allow: 'Allow',
  deny: 'Deny',
  attention: 'Attention',
  live: 'Live',
};

const useStyles = makeStyles({
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    height: '20px',
    paddingInline: '8px',
    borderRadius: tokens.borderRadiusCircular,
    fontSize: '11px',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  dot: { width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor', flexShrink: 0 },
  // Status colours (tokens.mjs): running/allow green, moving cyan, stopped neutral, failed/deny red.
  running: { color: vt.green, backgroundColor: vt.tintGreen },
  allow: { color: vt.green, backgroundColor: vt.tintGreen },
  starting: { color: vt.cyan, backgroundColor: vt.tintCyan },
  stopping: { color: vt.cyan, backgroundColor: vt.tintCyan },
  // Traffic moving right now (a live flow): cyan, like the map's live path.
  live: { color: vt.cyan, backgroundColor: vt.tintCyan },
  stopped: { color: vt.fg3, backgroundColor: vt.tintNeutral },
  failed: { color: vt.red, backgroundColor: vt.tintRed },
  deny: { color: vt.red, backgroundColor: vt.tintRed },
  attention: { color: vt.orange, backgroundColor: vt.tintOrange },
});

/** A state as a tinted, fully rounded chip with a dot: a wash of the state colour, never a fill. */
export function StatePill({ state, label, className }: { state: PillState; label?: string; className?: string }) {
  const s = useStyles();
  return (
    <span className={mergeClasses(s.pill, s[state], className)} data-state={state}>
      <span className={s.dot} aria-hidden />
      {label ?? LABEL[state]}
    </span>
  );
}
